import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getMaxCapacity } from "../../data";

import { useGetPartnerOffering } from "../../data/api";
import { buildPathWithParams, useQueryString } from "../../hooks";
import { useTrackProductViewedEcommerceEventOnMount } from "../../offering/analytics_ecommerce_events";
import {
  useLocationV2Query,
  usePartnerCatalogQueryMaybe,
  useSpaceV2Query,
} from "../../offering/api";
import { useQuoteQuery } from "../../offering/hooks/use_quote_query";
import {
  useCurrentQueryString,
  useSyncQueryStringV2,
} from "../../offering/offering_helpers";
import {
  normalizeMeetingRoomOrderParams,
  toQuoteInput,
  useDateTimePickerHelper,
  useMeetingRoomOrderParamsHandlers,
} from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import {
  CheckoutBoxMobile,
  MeetingRoomCheckoutBoxDesktopV2,
} from "../components/checkout_box";
import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import OfferingDetailsBase from "./template_offering_details_base";
import { useHandlePressBack } from "../hooks/use_handle_press_back";

export interface TemplateOfferingDetailsMeetingRoomParams {
  offeringID: string;
  spaceID: string;
}

export interface TemplateOfferingDetailsMeetingRoomQueryParams {
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  fromCatalog?: string;
  promoCode?: string;
}

interface TemplateOfferingDetailsMeetingRoomProps {
  partnerConfig: PartnerConfig;
}

export function TemplateOfferingDetailsMeetingRoom(
  props: TemplateOfferingDetailsMeetingRoomProps
) {
  const { partnerConfig } = props;
  const { firstDayOfWeek, showPriceInfoMessage = true } = partnerConfig;
  const pageParams = useParams<TemplateOfferingDetailsMeetingRoomParams>();
  const queryParams =
    useQueryString<TemplateOfferingDetailsMeetingRoomQueryParams>();
  const partnerOfferingID = pageParams.offeringID;
  const spaceID = pageParams.spaceID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );

  const {
    orderParams,
    orderParamsError,
    handleChangeDateRange,
    handleChangePromoCode,
    handleChangeAttendees,
  } = useMeetingRoomOrderParamsHandlers({
    ...normalizeMeetingRoomOrderParams({
      ...queryParams,
      promoCode: queryParams.promoCode,
    }),
    spaceID,
  });
  const {
    isOutsideRange,
    isBlockedDate,
    handleSelectDate,
    handleNextVisibleDates,
    availableDaysError,
    availableTimeSlotsError,
    timeslots,
    availableDays,
    disableRangeSelection,
  } = useDateTimePickerHelper(spaceID, {
    dateRange: {
      startDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.startDate,
      endDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.endDate,
    },
  });

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const history = useHistory();
  const space = useSpaceV2Query(spaceID);

  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();

  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(() => {
    return spaceID && spaceLayoutID
      ? toQuoteInput(
          spaceID,
          partnerOffering.partner.id,
          orderParams,
          "WL",
          {
            layoutID: spaceLayoutID,
            attendees: orderParams.attendees || 0,
          },
          undefined,
          availableDays
        )
      : null;
  }, [
    spaceID,
    partnerOffering.partner.id,
    orderParams,
    spaceLayoutID,
    availableDays,
  ]);
  const location = useLocationV2Query(space.locationID);

  const { data: quote } = useQuoteQuery(quoteInput);

  const queryString = useCurrentQueryString(orderParams);
  useSyncQueryStringV2(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/${partnerOfferingID}/spaces/${spaceID}/checkout?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, partnerOfferingID, spaceID, queryString]);

  const { handlePressBackFromDetailsPage } = useHandlePressBack();

  const offeringName = `${space.name}, ${location.name}`;

  useTrackProductViewedEcommerceEventOnMount(
    space.name,
    space,
    location,
    quote
  );

  const checkoutBox = (
    <MeetingRoomCheckoutBoxDesktopV2
      firstDayOfWeek={firstDayOfWeek}
      orderParams={orderParams}
      orderParamsErrors={orderParamsError}
      isOutsideRange={isOutsideRange}
      isBlockedDate={isBlockedDate}
      onSelectDate={handleSelectDate}
      onChangeAttendees={handleChangeAttendees}
      timeslots={timeslots}
      quote={quote}
      onChangeDateRange={handleChangeDateRange}
      onNextVisibleDates={handleNextVisibleDates}
      availableDaysError={availableDaysError}
      availableTimeSlotsError={availableTimeSlotsError}
      datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
      space={space}
      showPriceInfoMessage={showPriceInfoMessage}
      onContinue={handleContinue}
      availableDays={availableDays}
      disableRangeSelection={disableRangeSelection}
      showQuoteDetails
    />
  );

  return (
    <OfferingDetailsBase
      partnerOffering={partnerOffering}
      partnerOfferingID={partnerOfferingID}
      partnerConfig={partnerConfig}
      onPressBack={() => handlePressBackFromDetailsPage(partnerOfferingID)}
      selectedSpace={space}
      quoteID={quote?.id}
      orderParams={orderParams}
      data={{
        imagesMobile: space.images
          .map((i) => i.small)
          .concat(location.images.map((i) => i.small)),
        imagesDesktop: space.images
          .map((i) => i.medium)
          .concat(location.images.map((i) => i.medium)),
        imagesModal: space.images
          .map((i) => i.large)
          .concat(location.images.map((i) => i.large)),
        location: location,
        description: space.description && space.description.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: space.description }} />
        ),
        pageTitle: space.name,
        title: space.name || partnerOffering.offeringTitle,
        offeringName,
        capacity: getMaxCapacity(space),
      }}
      checkoutBoxDesktop={checkoutBox}
      checkoutBoxMobile={
        <CheckoutBoxMobile
          checkoutBox={checkoutBox}
          quote={quote}
          space={space}
        />
      }
    />
  );
}
